import React from 'react';
import { Loader2, Sparkles, RefreshCw, Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { publicAvisApi } from '../../lib/api';

/**
 * Assistant de rédaction d'un avis (2026-09-23, Emeni).
 *
 * « Les clients ont la flemme d'écrire, et ça les bloque : ils envoient des avis sans commentaire. »
 * Parcours voulu par Emeni : le client met sa note → on lui PROPOSE que l'assistant écrive pour lui →
 * les questions arrivent UNE PAR UNE, avec un bouton « Suivant » → à la fin seulement, trois textes au
 * choix, qu'il peut faire régénérer. Il ne tape rien, sauf s'il le veut (dernière question, facultative).
 *
 * CE QUI REND LA CHOSE HONNÊTE (et légale : art. L111-7-2 du Code de la consommation, règles de Google) :
 *   1. Les questions et le ton SUIVENT LA NOTE. Une note de 1 ou 2 mène à un avis négatif, jamais adouci.
 *      Ne jamais réserver l'assistant aux clients contents : ce serait du filtrage d'avis, interdit.
 *   2. Le texte ne part que des réponses du client, et l'écran le dit.
 *   3. Rien n'est envoyé sans qu'il ait choisi son texte ; il peut toujours le récrire.
 */

// Volontairement sobres (2026-09-23, Emeni : « je n'aime pas le style des commentaires de l'IA,
// "le résultat était impressionnant", il ne faut pas abuser »). Un client réel écrit peu et sans
// emphase ; des avis grandiloquents se repèrent, et Google filtre ce qui sent la publicité.
const POINTS_CONTENT = [
    'Travail soigné', 'Bon résultat', 'Taches parties', 'Ponctualité', 'Rapidité',
    'Bons conseils', 'Personne agréable', 'Matériel propre', 'Bon rapport qualité-prix', 'Rien à redire',
];
const POINTS_MITIGE = [
    'Résultat correct', 'Travail soigné', 'Personne agréable', 'Ponctualité',
    'Taches encore visibles', 'Retard', 'Prix un peu élevé', 'Manque de conseils', 'Séchage long',
];
const POINTS_MECONTENT = [
    'Résultat décevant', 'Taches encore là', 'Retard', 'Rendez-vous annulé', 'Travail bâclé',
    'Dégât sur le mobilier', 'Prix trop élevé', 'Mauvaise communication', 'Odeur persistante', 'Séchage interminable',
];
// Un restaurant ne parle pas comme un particulier (Emeni, 2026-09-23 : « elle doit savoir si c'est un
// client particulier ou professionnel, ce qui permet de savoir quelles questions poser »). Le type vient
// de l'avis lui-même (prestation_reviews.client_type), pas d'une devinette.
const POINTS_CONTENT_PRO = [
    'Travail soigné', 'Bon résultat', 'Taches parties', 'Ponctualité', 'Rapidité',
    'Intervention hors service', 'Équipe discrète', 'Salle prête à temps', 'Bon rapport qualité-prix', 'Rien à redire',
];
const POINTS_MECONTENT_PRO = [
    'Résultat décevant', 'Taches encore là', 'Retard', 'Intervention annulée', 'Travail bâclé',
    'Gêne pendant le service', 'Dégât sur le mobilier', 'Prix trop élevé', 'Mauvaise communication', 'Séchage trop long',
];
// L'état d'avant : c'est lui qui permet d'écrire « c'était taché, c'est parti ».
const AVANT_PARTICULIER = [
    'Taches anciennes', 'Taches récentes', 'Traces de nourriture', 'Poils d’animaux',
    'Odeur', 'Jamais nettoyé', 'Usage quotidien', "Pas très sale",
];
const AVANT_PRO = [
    'Taches anciennes', 'Passage intensif', 'Traces de nourriture', 'Auréoles',
    'Odeur', 'Jamais nettoyé', 'Nettoyage régulier', "Pas très sale",
];

const puce = (actif: boolean, couleur: string): React.CSSProperties => ({
    padding: '10px 14px', borderRadius: 999, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${actif ? couleur : '#e7e2da'}`,
    background: actif ? couleur : '#fff', color: actif ? '#fff' : '#3d3a35',
});
const bouton = (couleur: string, plein: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, cursor: 'pointer',
    padding: '12px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
    border: plein ? 0 : '1px solid #e7e2da', background: plein ? couleur : '#fff', color: plein ? '#fff' : '#3d3a35',
});

export function AssistantAvis({ token, note, prestation, objetsCatalogue, pro, couleur, onTexte, onRefus }: {
    token: string;
    note: number;
    /** Ce qui a été nettoyé, quand l'invitation vient d'une intervention enregistrée. Vide sinon. */
    prestation?: string;
    /** Prestations du catalogue, proposées au client quand on ignore ce qui a été nettoyé. */
    objetsCatalogue?: string[];
    /** Avis d'un client professionnel : questions et vocabulaire adaptés. */
    pro?: boolean;
    couleur: string;
    onTexte: (texte: string, reponses: any) => void;
    onRefus: () => void;
}) {
    // etape : -1 = la proposition de départ, 0..n-1 = les questions, n = les propositions de texte.
    const [etape, setEtape] = React.useState(-1);
    const [points, setPoints] = React.useState<string[]>([]);
    const [recommande, setRecommande] = React.useState('');
    const [reaction, setReaction] = React.useState('');
    const [libre, setLibre] = React.useState('');
    // Ce qui a été nettoyé, demandé UNIQUEMENT quand l'établissement ne l'a pas enregistré (Emeni,
    // 2026-09-23 : « quand on envoie des avis sur une adresse mail sans qu'elle ait le suivi de ce qui a
    // été fait, il faut qu'elle pose la question »).
    const [objet, setObjet] = React.useState('');
    const [avant, setAvant] = React.useState<string[]>([]);
    const [libreOuvert, setLibreOuvert] = React.useState(false);
    const objetInconnu = !prestation;
    const [propositions, setPropositions] = React.useState<string[]>([]);
    const [choisi, setChoisi] = React.useState(-1);
    const [occupe, setOccupe] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    // Les choix proposés suivent la note : on ne demande pas à un client mécontent ce qui l'a enchanté,
    // et suivent le type de client : un restaurant parle de salle et de service, pas de salon.
    const liste = note <= 2 ? (pro ? POINTS_MECONTENT_PRO : POINTS_MECONTENT)
        : note === 3 ? POINTS_MITIGE
            : (pro ? POINTS_CONTENT_PRO : POINTS_CONTENT);
    const listeAvant = pro ? AVANT_PRO : AVANT_PARTICULIER;
    React.useEffect(() => { setPoints([]); setPropositions([]); setChoisi(-1); setRecommande(''); setReaction(''); }, [note]);

    const basculer = (p: string) => setPoints((l) => l.includes(p) ? l.filter((x) => x !== p) : [...l, p]);
    const basculerAvant = (p: string) => setAvant((l) => l.includes(p) ? l.filter((x) => x !== p) : [...l, p]);
    const reponses = { points, recommande, reaction, libre: libre.trim(), objet: objet.trim(), avant };

    const questions = [
        ...(objetInconnu ? [{
            titre: "Qu'avez-vous fait nettoyer ?",
            aide: (objetsCatalogue || []).length ? 'Touchez ce qui correspond, ou écrivez-le.' : 'En deux mots.',
            corps: (
                <div>
                    {(objetsCatalogue || []).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                            {(objetsCatalogue || []).map((o) => (
                                <button key={o} type="button" onClick={() => setObjet(o)} style={puce(objet === o, couleur)}>{o}</button>
                            ))}
                        </div>
                    )}
                    <input value={objet} onChange={(e) => setObjet(e.target.value)} maxLength={120}
                        placeholder="Par exemple : mon canapé d'angle"
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e7e2da', borderRadius: 8, padding: 10, fontSize: 13.5, fontFamily: 'inherit' }} />
                </div>
            ),
            pret: objet.trim().length > 1,
        }] : []),
        {
            // Sans l'état d'avant, l'assistant ne peut pas écrire « c'était taché, c'est parti » — c'est
            // pourtant ce qui rend un avis concret (Emeni, 2026-09-23 : « niveau de salissure, résultat »).
            titre: "C'était dans quel état avant ?",
            aide: "Facultatif, mais c'est ce qui rend l'avis concret.",
            corps: (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {listeAvant.map((p) => (
                        <button key={p} type="button" onClick={() => basculerAvant(p)} style={puce(avant.includes(p), couleur)}>{p}</button>
                    ))}
                </div>
            ),
            pret: true,
            facultative: true,
        },
        {
            titre: note <= 2 ? "Qu'est-ce qui n'a pas été ?" : note === 3 ? "Comment ça s'est passé ?" : 'Qu’est-ce qui vous a marqué ?',
            aide: 'Touchez tout ce qui correspond.',
            corps: (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {liste.map((p) => (
                        <button key={p} type="button" onClick={() => basculer(p)} style={puce(points.includes(p), couleur)}>{p}</button>
                    ))}
                </div>
            ),
            pret: points.length > 0,
        },
        note <= 2 ? {
            titre: "L'établissement a-t-il réagi ?",
            aide: 'Cela change ce qui sera écrit.',
            corps: (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[['oui', 'Oui'], ['non', 'Non'], ['inconnu', 'Je ne sais pas']].map(([v, l]) => (
                        <button key={v} type="button" onClick={() => setReaction(v)} style={puce(reaction === v, couleur)}>{l}</button>
                    ))}
                </div>
            ),
            pret: !!reaction,
        } : {
            titre: 'Vous recommanderiez ?',
            aide: '',
            corps: (
                <div style={{ display: 'flex', gap: 8 }}>
                    {[['oui', 'Oui'], ['non', 'Non']].map(([v, l]) => (
                        <button key={v} type="button" onClick={() => setRecommande(v)} style={puce(recommande === v, couleur)}>{l}</button>
                    ))}
                </div>
            ),
            pret: !!recommande,
        },
    ];

    const generer = async () => {
        setOccupe(true); setErreur('');
        try {
            const r: any = await publicAvisApi.assistant(token, { rating: note, reponses, prestation });
            if (r?.error) { setErreur(r.error); return; }
            setPropositions(r.propositions || []);
            setChoisi(-1);
        } catch (e: any) { setErreur(e?.message || "L'assistant n'est pas disponible."); }
        finally { setOccupe(false); }
    };

    const suivant = async () => {
        if (etape < questions.length - 1) { setEtape(etape + 1); return; }
        setEtape(questions.length);
        await generer();
    };

    const cadre: React.CSSProperties = { border: '1px solid #ece8e0', borderRadius: 12, padding: 16, background: '#faf9f7', marginBottom: 18 };

    // Écran de départ : on propose, on n'impose pas.
    if (etape === -1) {
        return (
            <div style={cadre}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                    <Sparkles size={15} style={{ color: couleur }} />
                    <span style={{ fontWeight: 700, fontSize: 14.5, color: '#1c1a17' }}>Pas envie d'écrire ?</span>
                </div>
                <p style={{ fontSize: 13.5, color: '#3d3a35', lineHeight: 1.55, margin: '0 0 14px' }}>
                    Répondez à trois questions rapides, sans rien taper, et nous écrirons votre commentaire pour vous. Vous choisirez le texte final.
                </p>
                <div style={{ display: 'grid', gap: 8 }}>
                    <button type="button" onClick={() => setEtape(0)} style={bouton(couleur, true)}>
                        <Sparkles size={15} /> Oui, posez-moi les questions
                    </button>
                    <button type="button" onClick={onRefus} style={bouton(couleur, false)}>Non, j'écris moi-même</button>
                </div>
            </div>
        );
    }

    // Écran final : les propositions.
    if (etape >= questions.length) {
        return (
            <div style={cadre}>
                {occupe && !propositions.length ? (
                    <div style={{ textAlign: 'center', padding: '18px 0' }}>
                        <Loader2 size={22} className="animate-spin" style={{ color: couleur }} />
                        <p style={{ fontSize: 13.5, color: '#78736b', margin: '10px 0 0' }}>Nous écrivons votre commentaire…</p>
                    </div>
                ) : (
                    <>
                        <div style={{ fontWeight: 700, fontSize: 14.5, color: '#1c1a17', marginBottom: 10 }}>Choisissez le texte qui vous ressemble</div>
                        {erreur && <p style={{ color: '#b3453a', fontSize: 13, margin: '0 0 10px' }}>{erreur}</p>}
                        {propositions.map((p, i) => (
                            <button key={i} type="button" onClick={() => { setChoisi(i); onTexte(p, reponses); }}
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left', marginBottom: 8, cursor: 'pointer',
                                    border: `1px solid ${choisi === i ? couleur : '#e7e2da'}`, borderRadius: 10, padding: '12px 14px',
                                    background: '#fff', fontSize: 13.5, lineHeight: 1.55, color: '#3d3a35', fontFamily: 'inherit',
                                    boxShadow: choisi === i ? `0 0 0 2px ${couleur}22` : 'none',
                                }}>
                                {choisi === i && <Check size={13} style={{ color: couleur, marginRight: 6, verticalAlign: 'middle' }} />}
                                {p}
                            </button>
                        ))}
                        <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                            <button type="button" onClick={generer} disabled={occupe} style={bouton(couleur, false)}>
                                {occupe ? <><Loader2 size={15} className="animate-spin" /> Rédaction…</> : <><RefreshCw size={15} /> Proposer trois autres textes</>}
                            </button>
                            <button type="button" onClick={() => setEtape(questions.length - 1)} style={{ ...bouton(couleur, false), border: 0, background: 'none' }}>
                                <ArrowLeft size={14} /> Revenir aux questions
                            </button>
                        </div>
                        {propositions.length > 0 && (
                            <p style={{ fontSize: 11.5, color: '#a8a29a', lineHeight: 1.5, margin: '8px 0 0' }}>
                                Texte proposé à partir de vos réponses. Il sera publié en votre nom : vous pouvez le modifier juste en dessous.
                            </p>
                        )}
                    </>
                )}
            </div>
        );
    }

    // Les questions, une par une.
    const q = questions[etape];
    return (
        <div style={cadre}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#a8a29a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Question {etape + 1} sur {questions.length}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                    {questions.map((_, i) => (
                        <span key={i} style={{ width: 18, height: 3, borderRadius: 2, background: i <= etape ? couleur : '#e7e2da' }} />
                    ))}
                </div>
            </div>

            <div style={{ fontWeight: 700, fontSize: 15, color: '#1c1a17', marginBottom: q.aide ? 3 : 12 }}>{q.titre}</div>
            {q.aide && <p style={{ fontSize: 12.5, color: '#78736b', margin: '0 0 12px' }}>{q.aide}</p>}
            {q.corps}

            {/* Un mot en plus, sans en faire une question de plus : replié tant qu'on n'en veut pas
                (Emeni : « il ne faut pas trop de questions non plus »). */}
            {etape === questions.length - 1 && (
                libreOuvert ? (
                    <textarea value={libre} onChange={(e) => setLibre(e.target.value)} rows={2} maxLength={400}
                        placeholder="Un détail à ajouter (facultatif)"
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e7e2da', borderRadius: 8, padding: 10, fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', marginTop: 12 }} />
                ) : (
                    <button type="button" onClick={() => setLibreOuvert(true)}
                        style={{ background: 'none', border: 0, padding: 0, marginTop: 12, cursor: 'pointer', fontSize: 12.5, color: '#78736b', textDecoration: 'underline' }}>
                        Ajouter un détail (facultatif)
                    </button>
                )
            )}
            {erreur && <p style={{ color: '#b3453a', fontSize: 13, margin: '10px 0 0' }}>{erreur}</p>}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button type="button" onClick={() => setEtape(etape - 1)} style={{ ...bouton(couleur, false), flex: '0 0 auto', padding: '12px 14px' }}>
                    <ArrowLeft size={15} />
                </button>
                <button type="button" onClick={suivant} disabled={!q.pret && !q.facultative} style={{ ...bouton(couleur, true), flex: 1, opacity: (!q.pret && !q.facultative) ? 0.5 : 1 }}>
                    {etape === questions.length - 1
                        ? <><Sparkles size={15} /> Écrire mon commentaire</>
                        : <>{q.facultative || q.pret ? 'Suivant' : 'Choisissez au moins une réponse'} <ArrowRight size={15} /></>}
                </button>
            </div>
            {q.facultative && (
                <button type="button" onClick={suivant} style={{ ...bouton(couleur, false), border: 0, background: 'none', width: '100%', marginTop: 6, fontWeight: 600, color: '#78736b' }}>
                    Passer cette question
                </button>
            )}
        </div>
    );
}
